import {PayloadAction, createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {IFetchOrdersParams} from '../../api/modules/master-data/types'
import {searchOrder} from '../../api/modules/order'
import {RootState} from '../../store'
import {TCustomer, TOrder, TOrderChildern} from '../types'
import {IFetchAttacmentsParams} from 'src/services/types'

export interface IOrder {
  id: number
  customer_id: string
  regarding: null | null
  status: number
  created_at: string
  updated_at: string
  reason: null | string
  approved_level: number
  amandemen_parent: null | string
  po_number: string
  status_text: string
  children: Array<{
    id: number
    sip_order_id: null | number
    contract_id: null | number
    sales_org_code: string
    incoterm1_id: number
    incoterm2_id: number
    product_type: string
    product_name: string
    product_spec: string
    request_price: number
    fixed_price: number
    request_quantum: number
    fixed_quantum: number
    region_id: number
    province_id: number
    city_id: number
    sector_id: number
    end_user: string
    warehouse_id: string
    notes: null | string
    payment1_id: number
    payment2_id: number
    shipping_tolerance: number
    status: number
    created_at: string
    updated_at: string
    status_sip: number
    status_contract: number
    reason: null | string
    quantum_do: number
    sap_so_item_number: null | number | string
    material_number: null | number | string
    end_user_id: null | number | string
    status_text: string
    pivot: {
      order_sub_id: number
      order_sub_detail_id: number
    }
    incoterm1: {
      id: number
      code: string
      name: string
    } | null
    incoterm2: {
      id: number
      code: string
      name: string
    } | null
  }>
  customer: null | string
  amandemen: null | string
}

export interface ISearchOrderResult {
  current_page: number
  data: Array<IOrder>
  first_page_url: string
  from: 1
  last_page: 2
  last_page_url: string
  links: Array<{
    url: null
    label: '&laquo; Previous'
    active: false
  }>
  next_page_url: string
  path: string
  per_page: number
  prev_page_url: number | null
  to: number
  total: number
}

export interface IOrderSearches {
  orderNumber: string
  status: string
  sector: string
  startDate: string
  endDate: string
  buyer: string
  regarding: string
  quantum: string
  sales: string
  customer: string
}

export interface IFetchSearchOrdersResponse {
  code: number
  error: boolean
  message: string
  data: ISearchOrderResult
}

export interface IOrderState {
  loading: boolean
  error: string
  orders: Array<IOrder>
  order: IOrder | TOrderChildern | null
  searches: IOrderSearches
  searchResult: ISearchOrderResult | null
  orderFilter: IFetchOrdersParams
  customerFilter: Partial<TCustomer>
  customerList: Array<TCustomer>
  numberUrls: Array<{
    url: string
    label: string
    active: boolean
  }>
  showAttachmentModal: boolean
  attachments: any[]
  attachment_type: IFetchAttacmentsParams['attachment_type']
  isDownloadOnly: boolean
}

export const initialState: IOrderState = {
  loading: false,
  error: '',
  orders: [],
  order: null,
  searches: {
    orderNumber: '',
    status: '',
    sector: '',
    startDate: '',
    endDate: '',
    buyer: '',
    regarding: '',
    quantum: '',
    sales: '',
    customer: '',
  },
  searchResult: null,
  customerFilter: {},
  customerList: [],
  numberUrls: [],
  orderFilter: {
    per_page: 25,
    customer_name: '',
    po_number: '',
    status: '',
    customer_name_orderby: '',
    from_date: '',
    to_date: '',
    po_number_orderby: '',
    sector_id: '',
    total_request_quantum: '',
    flag: '',
    sales_org_code: '',
    regarding: '',
    total_request_price: '',
  },
  showAttachmentModal: false,
  attachments: [],
  attachment_type: 'header',
  isDownloadOnly: false,
}

interface IFetchSearchOrdersParams {
  credential: string
  po: string
  status: string
  sector: string
  date: string
  date2: string
  regarding: string
  quantum: string
  customer: string
  sales: string
  totalPrice: string
}

/**
 * Fetch search orders
 * @deprecated gunakan regular fetcher
 */
export const fetchSearchOrders = createAsyncThunk(
  'order/fetchSearchOrders',
  async (params: IFetchSearchOrdersParams) => {
    try {
      const {
        credential,
        po,
        status,
        sector,
        date,
        date2,
        regarding,
        quantum,
        customer,
        sales,
        totalPrice,
      } = params

      const response = await searchOrder(
        credential,
        po,
        status,
        sector,
        date,
        date2,
        regarding,
        quantum,
        customer,
        sales,
        totalPrice
      )
      return response.data as IFetchSearchOrdersResponse
    } catch (error) {
      return error
    }
  }
)

/**
 * Slices
 */
const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setOrders: (state: IOrderState, action: PayloadAction<Array<IOrder>>) => {
      state.orders = action.payload
    },
    setOrder: (state: IOrderState, action: PayloadAction<IOrderState['order']>) => {
      state.order = action.payload
    },
    setSearches: (state: IOrderState, action: PayloadAction<IOrderSearches>) => {
      state.searches = action.payload
    },
    setCustomerFilter: (state: IOrderState, action: PayloadAction<Partial<TCustomer>>) => {
      state.customerFilter = action.payload
    },
    setCustomerList: (state: IOrderState, action: PayloadAction<Array<TCustomer>>) => {
      state.customerList = action.payload
    },
    setNumberUrls: (state: IOrderState, action: PayloadAction<Array<any>>) => {
      state.numberUrls = action.payload
    },
    setOrderFilter: (state: IOrderState, action: PayloadAction<IFetchOrdersParams>) => {
      try {
        const serializedState = JSON.stringify(action.payload);
        localStorage.setItem('redux-order-filter', serializedState);
      } catch (e) {
      }
      state.orderFilter = action.payload
    },
    resetOrderFilter: (state: IOrderState) => {
      localStorage.removeItem('redux-order-filter');
      state.orderFilter = initialState.orderFilter
    },
    setShowAttachmentModal: (state, action) => {
      state.showAttachmentModal = action.payload
    },
    setAttachments: (state, action) => {
      state.attachments = action.payload
    },
    setAttachmentType: (state, action: PayloadAction<IOrderState['attachment_type']>) => {
      state.attachment_type = action.payload
    },
    setIsDownloadOnly: (state, action) => {
      state.isDownloadOnly = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSearchOrders.pending, (state: IOrderState) => ({
      ...state,
      loading: true,
    }))
    builder.addCase(fetchSearchOrders.fulfilled, (state: IOrderState, action) => ({
      ...state,
      loading: false,
      searchResult: (action?.payload as IFetchSearchOrdersResponse).data as ISearchOrderResult,
    }))
    builder.addCase(fetchSearchOrders.rejected, (state: IOrderState, action) => ({
      ...state,
      loading: false,
      error: action.error.message as string,
    }))
  },
})

/**
 * Actions
 */
export const {
  setOrder,
  setOrders,
  setSearches,
  setCustomerFilter,
  setCustomerList,
  setNumberUrls,
  setOrderFilter,
  resetOrderFilter,
  setShowAttachmentModal,
  setAttachments,
  setAttachmentType,
  setIsDownloadOnly,
} = orderSlice.actions

/**
 * Selector
 */
export const orderSelector = (state: RootState) => state.orderReducer

/**
 * Reducer
 */
export default orderSlice.reducer
